import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { NivelSituacaoTarefa } from "@workflow/types";
import { AlarmClock, ArrowRight, BellRing, Clock, ListTodo } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Caret, useSequentialTypewriter } from "@/components/shared/typewriter";
import { useNavigationHelpers } from "@/lib/navigation-helpers";
import { useAuth } from "@/lib/auth-context";
import { cn } from "@/lib/utils";

import { useAvisoTarefasVencidas } from "./use-aviso-tarefas-vencidas";

/** Pequeno atraso após ficar elegível, pra não "pipocar" durante o load da página. */
const ATRASO_ABERTURA_MS = 900;

interface TemaAviso {
  header: string;
  caret: string;
  destaque: string;
  caixa: string;
  relogio: string;
}

/** Paleta por gravidade: azul (aviso), âmbar (insistência), vermelho (restrição). */
const TEMAS: Record<NivelSituacaoTarefa, TemaAviso> = {
  [NivelSituacaoTarefa.NENHUM]: {
    header: "from-blue-500 to-indigo-600",
    caret: "bg-blue-500",
    destaque: "text-blue-600 dark:text-blue-400",
    caixa:
      "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-200",
    relogio: "text-blue-500",
  },
  [NivelSituacaoTarefa.AVISO]: {
    header: "from-blue-500 to-indigo-600",
    caret: "bg-blue-500",
    destaque: "text-blue-600 dark:text-blue-400",
    caixa:
      "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-200",
    relogio: "text-blue-500",
  },
  [NivelSituacaoTarefa.INSISTENCIA]: {
    header: "from-amber-500 to-orange-600",
    caret: "bg-amber-500",
    destaque: "text-amber-600 dark:text-amber-500",
    caixa:
      "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200",
    relogio: "text-amber-500",
  },
  [NivelSituacaoTarefa.RESTRICAO]: {
    header: "from-red-500 to-rose-600",
    caret: "bg-red-500",
    destaque: "text-red-600 dark:text-red-400",
    caixa:
      "border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200",
    relogio: "text-red-500",
  },
};

interface ConteudoAviso {
  badge: string;
  titulo: string;
  segments: string[];
}

/** Copy por gravidade: educado (aviso), firme (insistência), alarmante (restrição). */
function construirConteudo(
  nivel: NivelSituacaoTarefa,
  primeiroNome: string,
  quantidade: number,
): ConteudoAviso {
  const saudacao = primeiroNome ? `Olá, ${primeiroNome}. ` : "Olá. ";
  const tarefas = quantidade === 1 ? "1 tarefa atribuída" : `${quantidade} tarefas atribuídas`;

  if (nivel === NivelSituacaoTarefa.RESTRICAO) {
    return {
      badge: "Ação urgente necessária",
      titulo: "Seu acesso está em risco",
      segments: [
        `${saudacao}Suas tarefas exigem ação imediata.`,
        `Você está com ${tarefas} sem resposta há mais de 30 dias.`,
        "Este é o nível mais crítico de atraso — não dá para deixar passar.",
        "Seu acesso ao Workflow está sujeito a suspensão, e a reativação exigirá um pedido formal do seu gestor. Regularize estas tarefas com prioridade máxima. Quanto mais dias em atraso, mais tempo este aviso vai exigir para ser fechado.",
      ],
    };
  }

  if (nivel === NivelSituacaoTarefa.INSISTENCIA) {
    return {
      badge: "Atenção: tarefas atrasadas",
      titulo: "Suas tarefas continuam paradas",
      segments: [
        `${saudacao}Precisamos falar sobre suas tarefas pendentes.`,
        `Você está com ${tarefas} sem resposta há mais de 20 dias.`,
        "Elas já passaram bastante do prazo e seguem sem retorno.",
        "Regularize-as nos próximos dias. Se continuarem paradas, seu acesso a recursos do Workflow poderá ser afetado.",
      ],
    };
  }

  return {
    badge: "Lembrete de tarefas",
    titulo: "Tarefas aguardando sua resposta",
    segments: [
      `${saudacao}Este é um lembrete sobre suas tarefas no Workflow.`,
      `Você está com ${tarefas} sem resposta há mais de 15 dias.`,
      "Concluir ou responder cada uma ajuda a manter os fluxos de trabalho em dia.",
      "É rápido — assim que atualizá-las, este aviso deixa de aparecer. Contamos com você.",
    ],
  };
}

/** Primeiro nome do usuário, pra deixar o aviso mais pessoal (fallback neutro). */
function usePrimeiroNome(): string {
  const { user } = useAuth();
  return useMemo(() => user?.nome?.trim().split(/\s+/)[0] ?? "", [user?.nome]);
}

/**
 * Aviso (estilo "vamp", com texto digitado ao vivo) para quem tem tarefas
 * pendentes há mais de 15 dias. Aparece no máximo uma vez por dia por pessoa e o
 * botão de fechar — que sempre leva às tarefas pendentes — só é liberado após a
 * espera definida por nível no backend (5s no AVISO, 30s na INSISTENCIA; na
 * RESTRICAO começa em 60s e cresce com o atraso, até um teto — ver
 * `SituacaoTarefas.calcularSegundosParaDesbloquear` — além de ganhar copy de
 * suspensão).
 *
 * Montado uma vez em `MainLayout`, ao lado do `AgendaUpdateDialog`. Toda a
 * decisão de exibição vive em {@link useAvisoTarefasVencidas}; aqui é só a UI.
 */
export function TarefasVencidasDialog() {
  const { goToTarefaList } = useNavigationHelpers();
  const primeiroNome = usePrimeiroNome();
  const { aberto, quantidade, nivel, segundosTravado, registrarVisto } = useAvisoTarefasVencidas();
  const tema = TEMAS[nivel];

  // Abertura com pequeno atraso após ficar elegível (evita flash durante o load).
  const [visivel, setVisivel] = useState(false);
  useEffect(() => {
    if (!aberto) {
      setVisivel(false);
      return;
    }
    const timer = setTimeout(() => setVisivel(true), ATRASO_ABERTURA_MS);
    return () => clearTimeout(timer);
  }, [aberto]);

  const conteudo = useMemo(
    () => construirConteudo(nivel, primeiroNome, quantidade),
    [nivel, primeiroNome, quantidade],
  );
  const segments = conteudo.segments;

  const { typed, done, isTyping } = useSequentialTypewriter(segments, visivel, {
    charDelay: 14,
    segmentPause: 260,
  });

  // Contagem regressiva do botão: inicia quando o texto termina de aparecer.
  const [restante, setRestante] = useState(segundosTravado);
  const intervaloRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (!visivel || !done) {
      setRestante(segundosTravado);
      return;
    }
    setRestante(segundosTravado);
    intervaloRef.current = setInterval(() => {
      setRestante((s) => {
        if (s <= 1) {
          clearInterval(intervaloRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervaloRef.current);
  }, [visivel, done, segundosTravado]);

  const travado = restante > 0;

  const fechar = () => {
    if (travado) return;
    registrarVisto();
    setVisivel(false);
    goToTarefaList("atrasadas");
  };

  return (
    <Dialog open={visivel} onOpenChange={(open) => !open && fechar()}>
      <DialogContent
        // Bloqueia o fechamento por ESC/clique-fora e esconde o "X" padrão: o
        // usuário só sai pelo botão, e só depois da espera terminar.
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        className="flex max-h-[92dvh] w-[95vw] max-w-none flex-col gap-0 overflow-hidden rounded-3xl border-0 bg-card p-0 shadow-2xl sm:max-w-[720px] sm:rounded-3xl [&>button]:hidden"
      >
        {/* HEADER */}
        <div
          className={cn(
            "relative shrink-0 overflow-hidden bg-gradient-to-br p-6 pb-7 sm:p-10 sm:pb-9",
            tema.header,
          )}
        >
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: "radial-gradient(#fff 1px, transparent 1px)",
              backgroundSize: "16px 16px",
            }}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.5, rotate: -12 }}
            animate={{ opacity: 0.12, scale: 1, rotate: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="pointer-events-none absolute -right-6 -top-6 text-white sm:-right-8 sm:-top-10"
          >
            <AlarmClock className="h-40 w-40 sm:h-64 sm:w-64" />
          </motion.div>

          <DialogHeader className="relative z-10 text-left">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="mb-3 inline-flex w-fit items-center gap-2 rounded-full border border-white/25 bg-white/15 px-3 py-1 text-xs font-semibold text-white shadow-sm backdrop-blur-md sm:mb-4 sm:text-sm"
            >
              <BellRing className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              {conteudo.badge}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
            >
              <DialogTitle className="text-2xl font-bold leading-tight tracking-tight text-white sm:text-4xl">
                {conteudo.titulo}
              </DialogTitle>
            </motion.div>
          </DialogHeader>
        </div>

        {/* BODY: mensagem "digitada" ao vivo — rola internamente em telas baixas */}
        <div className="custom-scrollbar flex-1 space-y-4 overflow-y-auto bg-background p-6 text-base leading-relaxed text-foreground sm:space-y-5 sm:p-8 sm:text-lg">
          <p className="font-medium">
            {typed[0]}
            {isTyping(0) && <Caret className={tema.caret} />}
          </p>

          {typed[1] && (
            <p className={cn("flex items-start gap-2.5 font-semibold sm:gap-3", tema.destaque)}>
              <ListTodo className="mt-0.5 h-5 w-5 shrink-0 sm:h-6 sm:w-6" />
              <span>
                {typed[1]}
                {isTyping(1) && <Caret className={tema.caret} />}
              </span>
            </p>
          )}

          {typed[2] && (
            <p className="text-muted-foreground">
              {typed[2]}
              {isTyping(2) && <Caret className={tema.caret} />}
            </p>
          )}

          {typed[3] && (
            <p className={cn("rounded-xl border p-4 text-sm sm:text-base", tema.caixa)}>
              {typed[3]}
              {isTyping(3) && <Caret className={tema.caret} />}
            </p>
          )}
        </div>

        {/* FOOTER: explica por que o botão espera, sem "esconder" a razão em tooltip */}
        <DialogFooter className="flex shrink-0 flex-col gap-3 border-t border-border/50 bg-background p-6 pt-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:p-8 sm:pt-5">
          <p className="flex items-start gap-2 text-xs leading-relaxed text-muted-foreground sm:max-w-sm sm:text-sm">
            <Clock className={cn("mt-0.5 h-4 w-4 shrink-0", tema.relogio)} />
            {travado
              ? "Reservamos alguns segundos para garantir a leitura deste aviso. Em seguida o botão leva você às suas tarefas."
              : "Pronto. Você já pode revisar suas tarefas pendentes."}
          </p>

          <Button
            size="lg"
            onClick={fechar}
            disabled={travado}
            className={cn(
              "h-12 w-full shrink-0 text-base shadow-lg transition-all duration-300 sm:w-auto sm:px-8",
              travado && "cursor-not-allowed opacity-80",
            )}
          >
            {travado ? (
              <span className="tabular-nums">Ver minhas tarefas em {restante}s</span>
            ) : (
              <>
                Ver minhas tarefas
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
